/**
 * Tiny typed fetch wrapper for the Foreman REST API.
 *
 * All requests go through /api/*. In dev the Vite proxy forwards to :3001;
 * in prod Express serves both the built client and /api on :3001 — same
 * origin, no CORS, single fetch call works in both modes.
 *
 * Errors throw ApiError. Use TanStack Query's `isError` + `error` to render.
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  let finalBody: BodyInit | undefined;
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
    finalBody = JSON.stringify(body);
  }

  const res = await fetch(path, { ...init, method, headers, body: finalBody });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const msg = extractErrorMessage(parsed) ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

function extractErrorMessage(parsed: unknown): string | null {
  if (
    parsed &&
    typeof parsed === 'object' &&
    'error' in parsed &&
    parsed.error &&
    typeof parsed.error === 'object' &&
    'message' in parsed.error &&
    typeof (parsed.error as { message: unknown }).message === 'string'
  ) {
    return (parsed.error as { message: string }).message;
  }
  return null;
}

// ============================================================================
// Health
// ============================================================================

export interface HealthResponse {
  status: string;
  version: string;
  dbPath: string;
  projectsRoot: string;
  uptimeSeconds: number;
  projectCount: number;
}

export const getHealth = () => request<HealthResponse>('GET', '/api/health');

// ============================================================================
// Projects
//
// Color palette per ARCH §9: amber / sage / rust / teal / slate / plum / wheat.
// Final hex tuning is D-OPEN-1 — see client/src/lib/colors.ts for placeholders.
// ============================================================================

export type ColorTag =
  | 'amber'
  | 'sage'
  | 'rust'
  | 'teal'
  | 'slate'
  | 'plum'
  | 'wheat';

export type ProjectType =
  | 'ground_up_restaurant'
  | 'ti_buildout'
  | 'medical_dental'
  | 'insurance_restoration'
  | 'owners_rep_general'
  | 'warehouse_industrial';

export interface ProjectListItem {
  id: string;
  slug: string;
  name: string;
  project_type: ProjectType;
  color_tag: ColorTag;
  overall_status: string;
  city_state: string | null;
  construction_value: number | null;
  target_completion_date: string | null;
  actual_completion_date: string | null;
  documents_root_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectsListResponse {
  projects: ProjectListItem[];
  count: number;
}

export const listProjects = (params?: { status?: string; type?: string }) => {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.type) q.set('type', params.type);
  const qs = q.toString();
  return request<ProjectsListResponse>(
    'GET',
    `/api/projects${qs ? '?' + qs : ''}`
  );
};

export interface ProjectDetail {
  id: string;
  slug: string;
  name: string;
  project_type: ProjectType;
  template_id: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  address: string | null;
  city_state: string | null;
  zip: string | null;
  parcel_number: string | null;
  current_zone: string | null;
  overall_status: string;
  target_completion_date: string | null;
  actual_completion_date: string | null;
  construction_value: number | null;
  contract_sum_current: number | null;
  consulting_fee: number | null;
  fee_structure: string | null;
  show_construction_value_to_client: number;
  show_target_date_to_client: number;
  show_status_banner_to_client: number;
  /**
   * Boolean companion to the bcrypt `client_pin_hash` column. The raw hash
   * is stripped from every server response (E.7.3.a write-only secret
   * pattern, mirrors D-082's cloud_publish_secret_key). Use the
   * `set-pin` endpoint to mutate.
   */
  client_pin_configured: boolean;
  documents_root_path: string | null;
  private_scratchpad: string | null;
  color_tag: ColorTag;
  created_at: string;
  updated_at: string;
  is_archived: number;
  archived_at: string | null;
  published_last_updated: string | null;
  has_unpublished_changes: number;
  last_exported_at: string | null;
}

export interface PhaseSummary {
  id: string;
  phase_number: number;
  phase_name: string;
  phase_category: string | null;
  status: string;
  percent_complete: number;
  folder_path: string | null;
  is_visible_to_client: number;
  target_start_date: string | null;
  target_end_date: string | null;
  // D-118 — actual_start_date / actual_end_date dropped. Phase dates are
  // strictly manual via target_* now; no auto-stamp from task completion.
  default_duration_weeks: number | null;
  published_notes: string | null;
}

export interface ProjectDetailResponse {
  project: ProjectDetail;
  phases: PhaseSummary[];
  counts: Record<string, number>;
}

export const getProject = (id: string) =>
  request<ProjectDetailResponse>('GET', `/api/projects/${id}`);

// ----- Project create (Build E.2) -----

export interface CreateProjectBody {
  name: string;
  project_type: ProjectType;
  /** Optional template_id; without it, server falls back to default phases for type. */
  template_id?: string | null;
  color_tag?: ColorTag;
  client_name?: string | null;
  client_email?: string | null;
  client_phone?: string | null;
  address?: string | null;
  city_state?: string | null;
  zip?: string | null;
  construction_value?: number | null;
  consulting_fee?: number | null;
  fee_structure?: string | null;
  target_completion_date?: string | null;
  /**
   * Project START date (YYYY-MM-DD). Drives phase-target cascade math at
   * create time. Server defaults to today if omitted.
   */
  target_start_date?: string | null;
  overall_status?: 'Active' | 'On Hold' | 'Completed' | 'Archived' | 'Issue';
}

export interface CreateProjectResponse {
  project: ProjectDetail;
  phases: PhaseSummary[];
  scaffold: {
    project_folder: string;
    folders_created: number;
    folders_existing: number;
    phases_linked: number;
  };
}

export const createProject = (body: CreateProjectBody) =>
  request<CreateProjectResponse>('POST', '/api/projects', body);

// ----- Project edit (Build E.2 follow-up — per-project settings page) -----

/**
 * Status values per `001_initial_schema.sql:105`. `Archived` is excluded from
 * the edit UI on purpose — archiving has its own flow (E.4) that also moves
 * the on-disk folder; toggling the field directly via this PATCH would split
 * DB state from disk state.
 */
export const PROJECT_STATUSES_EDITABLE = [
  'Active',
  'On Hold',
  'Completed',
  'Issue',
] as const;
export type EditableProjectStatus = (typeof PROJECT_STATUSES_EDITABLE)[number];

export interface PatchProjectBody {
  name?: string;
  /**
   * Lowercase alphanumeric with single hyphens. Must be unique across all
   * projects. Run user input through `slugifyForApi` before sending.
   * Editable since D-083 follow-up so users can clean up legacy slug values.
   */
  slug?: string;
  client_name?: string | null;
  client_email?: string | null;
  client_phone?: string | null;
  address?: string | null;
  city_state?: string | null;
  zip?: string | null;
  parcel_number?: string | null;
  current_zone?: string | null;
  overall_status?: EditableProjectStatus;
  construction_value?: number | null;
  contract_sum_current?: number | null;
  consulting_fee?: number | null;
  fee_structure?: string | null;
  target_completion_date?: string | null;
  actual_completion_date?: string | null;
  private_scratchpad?: string | null;
  color_tag?: ColorTag;
}

export interface PatchProjectResponse {
  project: ProjectDetail;
  fields_updated: number;
}

/**
 * slugifyForApi — mirror of server `slugify()` in `server/api/projects.js`.
 * Used by the slug-edit UI to preview the normalized value as the user types
 * AND to send a clean value to PATCH. The server re-validates regardless,
 * so this is a UX helper, not a security boundary.
 */
export function slugifyForApi(s: string): string {
  return String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9-]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export const patchProject = (id: string, body: PatchProjectBody) =>
  request<PatchProjectResponse>('PATCH', `/api/projects/${id}`, body);

// ----- Project folder migration (Build E.3 — D-069) -----

export interface MigrateFolderBody {
  /** Absolute target path. Must live inside an allowed root. */
  new_root: string;
}

export interface MigrateFolderResponse {
  project_id: string;
  /** True if the on-disk move actually ran; false in idempotent recovery. */
  moved_on_disk: boolean;
  moved_from: string;
  moved_to: string;
  phases_rewritten: number;
  documents_rewritten: number;
  reindex:
    | {
        scanned: number;
        new: number;
        unchanged: number;
        superseded: number;
        deleted_on_disk: number;
        errors: Array<{ file: string; error: string }>;
      }
    | { error: string }
    | null;
}

export const migrateProjectFolder = (id: string, body: MigrateFolderBody) =>
  request<MigrateFolderResponse>(
    'POST',
    `/api/projects/${id}/migrate-folder`,
    body
  );

// ----- Archive / Unarchive (Build E.4 — D-070) -----

export interface ArchiveProjectResponse extends MigrateFolderResponse {
  archived_at: string;
}

export interface UnarchiveProjectResponse extends MigrateFolderResponse {
  unarchived_at: string;
}

export const archiveProject = (id: string, archiveRoot?: string) =>
  request<ArchiveProjectResponse>(
    'POST',
    `/api/projects/${id}/archive`,
    archiveRoot ? { archive_root: archiveRoot } : {}
  );

export const unarchiveProject = (id: string, activeRoot?: string) =>
  request<UnarchiveProjectResponse>(
    'POST',
    `/api/projects/${id}/unarchive`,
    activeRoot ? { active_root: activeRoot } : {}
  );

// ----- Project delete (Build E.5 — D-073) -----

export interface DeleteProjectResponse {
  project_id: string;
  deleted: true;
  /** The on-disk folder path that's PRESERVED after the DB row is removed. */
  preserved_folder_path: string | null;
}

export const deleteProject = (id: string) =>
  request<DeleteProjectResponse>('DELETE', `/api/projects/${id}`);

// ----- Client portal PIN (Build E.7.3.a / E.7.3.d) -----

export interface SetProjectPinBody {
  /**
   * The new PIN value. Empty string OR null clears the existing hash. Any
   * non-empty 4-100 char string sets a new bcrypt hash on the server.
   */
  pin: string | null;
}

export interface SetProjectPinResponse {
  project_id: string;
  client_pin_configured: boolean;
  updated_at: string;
}

export const setProjectPin = (id: string, body: SetProjectPinBody) =>
  request<SetProjectPinResponse>('POST', `/api/projects/${id}/set-pin`, body);

// ----- Photos (Build E.7.2 — folder-driven; drag-drop upload deferred) -----

export interface Photo {
  id: string;
  project_id: string;
  phase_id: string | null;
  /** D-128 — FK to albums.id. Photos with album_id=null are unsorted
   *  and never publish to the portal (PM-only). Album visibility is
   *  the unit of publication. */
  album_id: string | null;
  document_name: string;
  document_type: 'Photo';
  file_path: string;
  file_name_on_disk: string | null;
  file_size_kb: number | null;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
  /** D-124 — when 1, cloudPublisher pushes this photo to the portal on
   *  the next publish. Default 0 (PM-internal). PM toggles per-photo
   *  via ProjectPhotosPage.
   *  D-128 — secondary gate: photo only publishes IF this is 1 AND
   *  it belongs to a visible album. Default is now 1 (opt-out from D-125). */
  is_visible_to_portal: number;
  /** D-124 — PM-authored headline that captions the photo on the portal.
   *  NULL → portal falls back to document_name. */
  portal_title: string | null;
  /** Joined from the phases table — null when phase_id is null */
  phase_number: number | null;
  phase_name: string | null;
}

/** D-128 — album-centric photo organization. Each album has a name +
 *  optional phase + sub_category + visibility flag. Photos live inside
 *  albums (documents.album_id); the album is the publish unit.
 *  D-129 — `event_date` is the PM-settable date stamp for the album
 *  (distinct from row-inserted created_at). Nullable. */
export interface Album {
  id: string;
  project_id: string;
  name: string;
  description: string | null;
  phase_id: string | null;
  sub_category: string | null;
  event_date: string | null;
  is_visible_to_portal: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
  /** Photo count joined server-side for cheap display. */
  photo_count: number;
}

export interface ListAlbumsResponse {
  project_id: string;
  count: number;
  albums: Album[];
}

export interface CreateAlbumBody {
  name: string;
  description?: string | null;
  phase_id?: string | null;
  sub_category?: string | null;
  /** D-129 — ISO 8601 YYYY-MM-DD or null. */
  event_date?: string | null;
  is_visible_to_portal?: 0 | 1 | boolean;
}

export interface PatchAlbumBody {
  name?: string;
  description?: string | null;
  phase_id?: string | null;
  sub_category?: string | null;
  /** D-129 — ISO 8601 YYYY-MM-DD; null/'' to clear. */
  event_date?: string | null;
  is_visible_to_portal?: 0 | 1 | boolean;
  sort_order?: number;
}

export const listAlbums = (projectId: string) =>
  request<ListAlbumsResponse>('GET', `/api/projects/${encodeURIComponent(projectId)}/albums`);

export const createAlbum = (projectId: string, body: CreateAlbumBody) =>
  request<{ album: Album }>(
    'POST',
    `/api/projects/${encodeURIComponent(projectId)}/albums`,
    body,
  );

export const patchAlbum = (albumId: string, body: PatchAlbumBody) =>
  request<{ album: Album; changed: boolean }>(
    'PATCH',
    `/api/albums/${encodeURIComponent(albumId)}`,
    body,
  );

export const deleteAlbum = (albumId: string) =>
  request<{ ok: true; deleted_photos: number }>(
    'DELETE',
    `/api/albums/${encodeURIComponent(albumId)}`,
  );

/** D-128 — hard-delete a single photo. Removes the documents row + the
 *  file on disk. Project gets has_unpublished_changes=1 so the next
 *  auto-publish sweeps it from the portal. */
export const deletePhoto = (photoId: string) =>
  request<{ ok: true }>(
    'DELETE',
    `/api/photos/${encodeURIComponent(photoId)}`,
  );

export interface ListPhotosResponse {
  project_id: string;
  count: number;
  photos: Photo[];
}

/**
 * List a project's photos. Pass `phaseId` to scope to one phase, or
 * `'null'` (string) to surface only project-level photos with phase_id IS NULL.
 */
export const listPhotos = (projectId: string, phaseId?: string | null) => {
  let qs = '';
  if (phaseId !== undefined) {
    qs = `?phase_id=${phaseId === null ? 'null' : encodeURIComponent(phaseId)}`;
  }
  return request<ListPhotosResponse>('GET', `/api/projects/${projectId}/photos${qs}`);
};

// D-102 cleanup — `reindexPhotos` + its `ReindexPhotosResponse` interface
// removed. Was only called from PhotosSection (deleted in D-101). The
// server endpoint (`POST /api/projects/:id/photos/index`) stays in place
// — re-add the client helper when per-phase Re-index returns on a
// future surface (probably ProjectPhotosPage redesign or admin utility).

/**
 * URL the browser uses for a photo's raw bytes — same-origin so cookies
 * (none for local; future-proof) ride naturally and the response can be
 * dropped straight into an <img src=...>.
 */
export const photoRawUrl = (documentId: string): string =>
  `/api/photos/${encodeURIComponent(documentId)}/raw`;

/** D-124 — PATCH a photo's portal-visibility flag and/or PM-authored
 *  title. Omit a field to leave it unchanged. `portal_title` may be null
 *  or empty-string to clear back to "use document_name as fallback". */
export interface PatchPhotoBody {
  is_visible_to_portal?: 0 | 1 | boolean;
  portal_title?: string | null;
}

export interface PatchPhotoResponse {
  photo: Photo;
  changed: boolean;
}

export const patchPhoto = (documentId: string, body: PatchPhotoBody) =>
  request<PatchPhotoResponse>(
    'PATCH',
    `/api/photos/${encodeURIComponent(documentId)}`,
    body,
  );

export interface UploadPhotoResponse {
  ok: true;
  photo: Photo & {
    already_registered: boolean;
  };
}

/**
 * Upload a photo via multipart/form-data. Server saves to
 * `<project>/_Photos/general/<filename>` by default; when a `phaseId`
 * is supplied, the file is moved into `<project>/_Photos/<NN_PhaseName>/`
 * after staging. Server validates extension (image only) and size
 * (≤50 MB).
 *
 * D-126 — phase is now OPTIONAL. Per C: most photos belong to a flat
 * project-wide photo base (weekly construction updates, exterior
 * progress, etc.); phase tagging is a secondary classification used
 * only when the PM wants to slot a specific shot to a specific phase.
 *
 * D-125 — accepts optional `portalTitle` (PM-authored headline) +
 * `isVisibleToPortal` (default true, opt-out). When already_registered
 * (the file hash matched an existing row), the server leaves the
 * existing portal state alone.
 *
 * Uses native fetch instead of `request<T>` because the body is FormData,
 * not JSON.
 */
export async function uploadPhoto(
  projectId: string,
  file: File,
  opts: {
    /** D-128 — album the photo belongs to. When set, file lands in
     *  `<project>/_Photos/<album_id>/<filename>` and documents.album_id
     *  is bound. Omit for unsorted photos (PM-only, won't publish). */
    albumId?: string | null;
    /** D-126 — optional phase tag. Omit / null for a project-level photo. */
    phaseId?: string | null;
    portalTitle?: string | null;
    /** Default true. Set to false to upload as PM-internal only. */
    isVisibleToPortal?: boolean;
  } = {},
): Promise<UploadPhotoResponse> {
  const fd = new FormData();
  fd.append('photo', file);
  if (opts.albumId) {
    fd.append('album_id', opts.albumId);
  }
  if (opts.phaseId) {
    fd.append('phase_id', opts.phaseId);
  }
  if (opts.portalTitle && opts.portalTitle.trim()) {
    fd.append('portal_title', opts.portalTitle.trim());
  }
  if (opts.isVisibleToPortal === false) {
    fd.append('is_visible_to_portal', '0');
  }
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/photos/upload`,
    { method: 'POST', body: fd },
  );
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && body !== null && 'error' in body
        ? (body as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`
        : `HTTP ${res.status}`;
    throw new Error(message);
  }
  return body as UploadPhotoResponse;
}

export const MAX_PHOTO_UPLOAD_BYTES = 50 * 1024 * 1024;

// D-102 cleanup — client uploads helpers (`getClientUploadsStatus`,
// `pullClientUploads`) + their types removed. Were only called from
// PhotosSection (deleted D-101). Server endpoints stay in place
// (`GET .../client-uploads/status`, `POST .../pull-client-uploads`) +
// the R2 pull infrastructure on the server is intact (E.7.4 / D-087);
// re-add the client helpers when the Pull Client Uploads workflow
// returns on a future surface.

// ----- Cloud publish — Cloudflare D1 (Build E.7.1 — D-080 + D-081 pivot) -----

export interface PublishConfig {
  provider: string;                              // 'cloudflare_d1' for now
  account_id: string | null;
  database_id: string | null;
  account_id_source: 'db' | 'env' | 'none';
  database_id_source: 'db' | 'env' | 'none';
  secret_key_configured: boolean;
  secret_key_source: string;
  ready: boolean;
}

export interface PublishCounts {
  projects: number;
  phases: number;
  tasks: number;
  permits: number;
  activity_log: number;
}

export interface PublishPreviewResponse {
  project_id: string;
  project_name: string;
  last_published_at: string | null;
  no_change_since_last_publish: boolean;
  counts: PublishCounts;
  hash: string;
  sample: {
    first_visible_phase: string | null;
    first_visible_task: string | null;
    first_visible_permit: string | null;
    first_published_activity: string | null;
  };
}

export interface PublishResponse {
  ok: boolean;
  no_change: boolean;
  reason?: string;
  counts: PublishCounts;
  hash: string;
  rows_written?: PublishCounts;
  error?: string;
}

export const getPublishConfig = () =>
  request<PublishConfig>('GET', '/api/publish/config');

export const previewPublish = (projectId: string) =>
  request<PublishPreviewResponse>('POST', `/api/publish/${projectId}/preview`, {});

export const publishProjectToCloud = (projectId: string, force?: boolean) =>
  request<PublishResponse>('POST', `/api/publish/${projectId}`, force ? { force: true } : {});

/** Locked palette per ARCH §9. */
export const COLOR_TAGS: ColorTag[] = [
  'amber',
  'sage',
  'rust',
  'teal',
  'slate',
  'plum',
  'wheat',
];

/** Server-side enum (matches `ALLOWED_PROJECT_TYPES` in projects.js). */
export const PROJECT_TYPES: ProjectType[] = [
  'ground_up_restaurant',
  'ti_buildout',
  'medical_dental',
  'insurance_restoration',
  'owners_rep_general',
  'warehouse_industrial',
];

export function prettyProjectType(t: ProjectType): string {
  return t
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * The user's locked project-folder naming convention (memory:
 * project_folder_paths.md):
 *   <2-5 char brand abbrev><2-4 digit job number>_<City>_<State 2-letter>
 * e.g., WDG012_Columbus_OH, DDS014_Dayton_OH, WDG013_Pittsburgh_PA.
 *
 * The `+ New project` UI uses this to WARN the user when their project name
 * deviates — but does NOT hard-block, since exotic names are sometimes
 * legitimate and the server ultimately stores whatever is supplied.
 */
export const PROJECT_NAME_CONVENTION =
  /^[A-Z]{2,5}\d{2,4}_[A-Za-z][A-Za-z]*(_[A-Za-z][A-Za-z]*)*_[A-Z]{2}$/;

// ============================================================================
// Notes
//
// Pre-created from the project's template at create time (one row per section
// in `notes_sections_json`). Typical lifecycle is just PATCH for the auto-save
// loop. POST exists for adding custom sections beyond the template defaults
// (also used by the empty-state "Initialize sections from template" path for
// projects that predate v1.2 backend additions).
// ============================================================================

export interface Note {
  id: string;
  project_id: string;
  section_key: string;
  section_display_name: string;
  private_content: string;
  last_keystroke_at: string | null;
  last_persisted_at: string;
  last_exported_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface NotesListResponse {
  notes: Note[];
  count: number;
}

export const listNotes = (projectId: string) =>
  request<NotesListResponse>('GET', `/api/projects/${projectId}/notes`);

export interface NoteResponse {
  note: Note;
}

export const getNote = (id: string) =>
  request<NoteResponse>('GET', `/api/notes/${id}`);

export interface CreateNoteBody {
  section_key: string;
  section_display_name: string;
  private_content?: string;
  last_keystroke_at?: string;
  sort_order?: number;
}

export const createNote = (projectId: string, body: CreateNoteBody) =>
  request<NoteResponse>('POST', `/api/projects/${projectId}/notes`, body);

export interface PatchNoteBody {
  private_content?: string;
  last_keystroke_at?: string | null;
  sort_order?: number;
}

export interface PatchNoteResponse {
  note: Note;
  fields_updated: number;
}

export const patchNote = (id: string, body: PatchNoteBody) =>
  request<PatchNoteResponse>('PATCH', `/api/notes/${id}`, body);

export const deleteNote = (id: string) =>
  request<void>('DELETE', `/api/notes/${id}`);

// ============================================================================
// Permits — structured permit tracker (ARCH §7).
//
// Distinct from the Permitting notes section (narrative) and the Permitting
// phase tasks (rollups). Each row tracks status, jurisdiction, fees, dates,
// expiration, and a required-items checklist (JSON array). Server-side, the
// PATCH endpoint auto-sets has_unpublished_changes=1 on any field change
// (unless the caller explicitly sends has_unpublished_changes); intent is
// the orange-dot indicator surfaces unpublished state to the user once the
// client portal exists in Build E. v1 of the page does not render this
// indicator yet — see C.2.5 follow-ups when Build E lands.
// ============================================================================

export const PERMIT_STATUSES = [
  'Not Started',
  'In Preparation',
  'Submitted',
  'Under Review',
  'Issued',
  'Denied',
  'Withdrawn',
  'Expired',
] as const;
export type PermitStatus = (typeof PERMIT_STATUSES)[number];

/**
 * Single row inside a permit's `required_items_json` array.
 * Status uses the same red/amber/green vocabulary as everywhere else
 * (D-046): Missing → blocked / not started, In Process → in flight,
 * Done → complete.
 */
export interface RequiredItem {
  item: string;
  status: 'Missing' | 'In Process' | 'Done';
  note?: string;
}

export interface Permit {
  id: string;
  project_id: string;
  phase_id: string | null;
  permit_type: string;
  jurisdiction: string | null;
  permit_number: string | null;
  status: PermitStatus;
  application_date: string | null;
  issued_date: string | null;
  expiration_date: string | null;
  application_fee: number | null;
  permit_fee: number | null;
  /** Server returns parsed object; client sends object on POST/PATCH. */
  required_items_json: { items?: RequiredItem[] } | RequiredItem[] | null;
  private_notes: string;
  published_summary: string;
  is_visible_to_client: number;
  has_unpublished_changes: number;
  document_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PermitsListResponse {
  permits: Permit[];
  count: number;
}

export const listPermits = (projectId: string) =>
  request<PermitsListResponse>(
    'GET',
    `/api/projects/${projectId}/permits`
  );

export interface PermitResponse {
  permit: Permit;
}

export const getPermit = (id: string) =>
  request<PermitResponse>('GET', `/api/permits/${id}`);

export interface CreatePermitBody {
  permit_type: string;
  jurisdiction?: string | null;
  permit_number?: string | null;
  status?: PermitStatus;
  application_date?: string | null;
  issued_date?: string | null;
  expiration_date?: string | null;
  application_fee?: number | null;
  permit_fee?: number | null;
  required_items_json?: { items?: RequiredItem[] } | RequiredItem[] | null;
  private_notes?: string;
  published_summary?: string;
  is_visible_to_client?: 0 | 1 | boolean;
  phase_id?: string | null;
  document_id?: string | null;
}

export const createPermit = (projectId: string, body: CreatePermitBody) =>
  request<PermitResponse>(
    'POST',
    `/api/projects/${projectId}/permits`,
    body
  );

export interface PatchPermitBody {
  permit_type?: string;
  jurisdiction?: string | null;
  permit_number?: string | null;
  status?: PermitStatus;
  application_date?: string | null;
  issued_date?: string | null;
  expiration_date?: string | null;
  application_fee?: number | null;
  permit_fee?: number | null;
  required_items_json?: { items?: RequiredItem[] } | RequiredItem[] | null;
  private_notes?: string;
  published_summary?: string;
  is_visible_to_client?: 0 | 1 | boolean;
  has_unpublished_changes?: 0 | 1 | boolean;
  phase_id?: string | null;
  document_id?: string | null;
}

export const patchPermit = (id: string, body: PatchPermitBody) =>
  request<PermitResponse>('PATCH', `/api/permits/${id}`, body);

export const deletePermit = (id: string) =>
  request<void>('DELETE', `/api/permits/${id}`);

// ============================================================================
// File open — POST /api/files/open
//
// Local-server endpoint that opens a file or folder via OS file association
// (Windows: `start "" "<path>"`, macOS: `open`, Linux: `xdg-open`). Path is
// validated against allowed roots (project documents_root, backup_root, and
// each project's documents_root_path). See server/api/files.js + D-037.
//
// `folder_path` + `ensure_exists: true` auto-creates the folder if missing
// (still bounded by the allowed-roots check). Used by the Documents tracker
// page so legacy projects (created before D-029 added the
// `_Contracts/{Pre-Construction,Construction,Closeout}` split) get their
// category subfolders made on first click.
// ============================================================================

export interface OpenFileBody {
  file_path?: string;
  folder_path?: string;
  document_id?: string;
  ensure_exists?: boolean;
}

export interface OpenFileResponse {
  opened: string;
  command: string;
  platform: string;
}

export const openFile = (body: OpenFileBody) =>
  request<OpenFileResponse>('POST', '/api/files/open', body);

// ============================================================================
// Tracked plans — Plans tracker (C.6, D-056)
//
// Mirrors `tracked_documents` shape with adjustments for plans:
//   - 9-discipline taxonomy instead of 3 contract categories
//   - 5-state status (In Review / Revisions Requested / Approved / Stamped /
//     Superseded) — richer review lifecycle
//   - `document_id` is the canonical DocumentLinker target (Stamped Final
//     Set = one PDF artifact)
//
// Server: server/api/tracked_plans.js. Disk: <project>/_Plans/<stage>/
// <display_name>/. Private to PM; never crosses to client portal.
//
// D-089: stages replaced disciplines (free-text, template-driven defaults).
// Status set expanded with 'Awaiting' as the new "preloaded but not yet
// received" baseline.
// ============================================================================

export const TRACKED_PLAN_STATUSES = [
  'Awaiting',
  'In Review',
  'Revisions Requested',
  'Approved',
  'Stamped',
  'Superseded',
] as const;
export type TrackedPlanStatus = (typeof TRACKED_PLAN_STATUSES)[number];

export interface TrackedPlan {
  id: string;
  project_id: string;
  /** Free-text stage name (e.g. "Permit Set", "Pre-Architectural"). D-089. */
  stage: string;
  display_name: string;
  status: TrackedPlanStatus;
  private_notes: string;
  document_id: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface TrackedPlansListResponse {
  tracked_plans: TrackedPlan[];
  count: number;
}

export const listTrackedPlans = (projectId: string) =>
  request<TrackedPlansListResponse>(
    'GET',
    `/api/projects/${projectId}/tracked-plans`
  );

export interface CreateTrackedPlanBody {
  stage: string;
  display_name: string;
  status?: TrackedPlanStatus;
  private_notes?: string;
  document_id?: string | null;
  sort_order?: number;
}

export interface TrackedPlanResponse {
  tracked_plan: TrackedPlan;
}

export const createTrackedPlan = (
  projectId: string,
  body: CreateTrackedPlanBody
) =>
  request<TrackedPlanResponse>(
    'POST',
    `/api/projects/${projectId}/tracked-plans`,
    body
  );

export interface PatchTrackedPlanBody {
  stage?: string;
  display_name?: string;
  status?: TrackedPlanStatus;
  private_notes?: string;
  document_id?: string | null;
  sort_order?: number;
}

export const patchTrackedPlan = (id: string, body: PatchTrackedPlanBody) =>
  request<TrackedPlanResponse>('PATCH', `/api/tracked-plans/${id}`, body);

export const deleteTrackedPlan = (id: string) =>
  request<void>('DELETE', `/api/tracked-plans/${id}`);

// D-089 — bulk stage operations. Rename or delete every row in a stage.
// Used by the Plans tracker UI's stage rename + stage delete affordances.

export interface RenamePlanStageBody {
  from: string;
  to: string;
}

export interface RenamePlanStageResponse {
  rows_updated: number;
  from: string;
  to: string;
}

export const renamePlanStage = (
  projectId: string,
  body: RenamePlanStageBody
) =>
  request<RenamePlanStageResponse>(
    'PATCH',
    `/api/projects/${projectId}/tracked-plans/stages`,
    body
  );

export interface DeletePlanStageResponse {
  rows_deleted: number;
  stage: string;
}

export const deletePlanStage = (projectId: string, stageName: string) =>
  request<DeletePlanStageResponse>(
    'DELETE',
    `/api/projects/${projectId}/tracked-plans/stages/${encodeURIComponent(stageName)}`
  );

// ============================================================================
// Search — global FTS5 with optional AI synthesis (D.6 polish)
//
// Server: server/api/search.js. ARCH §8.4. Two modes:
//   - 'match' (default) — flat ranked list across all 5 indexed sources
//   - 'ai'              — match results + a synthesized paragraph
//
// Optional scope: 'all' (default) or 'project' (requires project_id).
// ============================================================================

export type SearchMode = 'match' | 'ai';
export type SearchScope = 'all' | 'project';
export type SearchSource =
  | 'note'
  | 'activity_log'
  | 'task'
  | 'permit'
  | 'tracked_document'
  | 'tracked_plan'
  | 'pay_app';

export interface SearchResult {
  source: string; // server's `src.label` value (e.g., 'note', 'permit')
  id: string;
  project_id: string;
  project_name: string | null;
  project_color: ColorTag | null;
  title: string;
  snippet: string; // contains <mark>...</mark> FTS5 highlights
  score: number;
  extra?: Record<string, unknown> | null;
}

export interface SearchAIResult {
  text: string;
  model: string;
  provider: string;
  tokens_in?: number;
  tokens_out?: number;
  latency_ms?: number;
}

export interface SearchResponse {
  results: SearchResult[];
  count: number;
  fts_query: string | null;
  ai?: SearchAIResult | null;
}

export type SearchAIProvider = 'mock' | 'local' | 'cloud';

export interface RunSearchOpts {
  scope?: SearchScope;
  project_id?: string;
  mode?: SearchMode;
  limit?: number;
  source?: SearchSource[];
  /** Used in AI mode — overrides app_settings.ai_default_target. Defaults
   *  to 'mock' from the SearchPanel UI so AI mode always returns something
   *  even when no local AI provider is running. */
  provider?: SearchAIProvider;
}

export const runSearch = (query: string, opts?: RunSearchOpts) => {
  const q = new URLSearchParams();
  q.set('q', query);
  if (opts?.scope) q.set('scope', opts.scope);
  if (opts?.project_id) q.set('project_id', opts.project_id);
  if (opts?.mode) q.set('mode', opts.mode);
  if (opts?.limit) q.set('limit', String(opts.limit));
  if (opts?.source && opts.source.length > 0)
    q.set('source', opts.source.join(','));
  if (opts?.provider) q.set('provider', opts.provider);
  return request<SearchResponse>('GET', `/api/search?${q.toString()}`);
};

// ============================================================================
// Indexed documents — auto-discovered files in the project folder tree
//
// The server walks the project folder on demand (`POST .../documents/index`)
// and registers files into the `documents` table. The DocumentLinker UI
// (C.4) reads this list to attach a canonical file to a permit (and later
// a task or other entity) via that entity's `document_id` column.
//
// Naming note: the interface is `IndexedDocument`, not `Document`, so it
// doesn't shadow `globalThis.Document` (the DOM type) in components that
// also use `document.querySelector` etc.
// ============================================================================

export interface IndexedDocument {
  id: string;
  project_id: string;
  phase_id: string | null;
  document_name: string | null;
  document_type: string | null;
  file_path: string;
  file_name_on_disk: string;
  file_size_kb: number | null;
  file_hash_sha256: string | null;
  version_label: string | null;
  is_current_version: 0 | 1;
  status: string | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListDocumentsResponse {
  documents: IndexedDocument[];
  count: number;
}

export const listDocuments = (
  projectId: string,
  params?: { all?: boolean; phase_id?: string; type?: string }
) => {
  const q = new URLSearchParams();
  if (params?.all) q.set('all', 'true');
  if (params?.phase_id) q.set('phase_id', params.phase_id);
  if (params?.type) q.set('type', params.type);
  const qs = q.toString();
  return request<ListDocumentsResponse>(
    'GET',
    `/api/projects/${projectId}/documents${qs ? '?' + qs : ''}`
  );
};

/** Re-scan the project folder and update the documents registry. Returns
 *  counts so the UI can show a brief "Found N new files" toast. */
export interface IndexResult {
  scanned: number;
  new: number;
  unchanged: number;
  superseded: number;
  deleted_on_disk: number;
  errors: Array<{ file: string; error: string }>;
}

export const reindexDocuments = (projectId: string) =>
  request<{ index_result: IndexResult }>(
    'POST',
    `/api/projects/${projectId}/documents/index`
  );

// ============================================================================
// Tracked documents — Documents page (ARCH §5)
//
// Three fixed categories (Pre-Construction / Construction / Closeout) and
// three states (Missing / In Process / Executed). Private to PM; never
// crosses to client portal. See server/api/tracked_documents.js.
// ============================================================================

export const TRACKED_DOC_CATEGORIES = [
  'Pre-Construction',
  'Construction',
  'Closeout',
] as const;
export type TrackedDocCategory = (typeof TRACKED_DOC_CATEGORIES)[number];

export const TRACKED_DOC_STATUSES = [
  'Missing',
  'In Process',
  'Executed',
] as const;
export type TrackedDocStatus = (typeof TRACKED_DOC_STATUSES)[number];

export interface TrackedDocument {
  id: string;
  project_id: string;
  category: TrackedDocCategory;
  display_name: string;
  status: TrackedDocStatus;
  private_notes: string;
  document_id: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface TrackedDocumentsListResponse {
  tracked_documents: TrackedDocument[];
  count: number;
}

export const listTrackedDocuments = (projectId: string) =>
  request<TrackedDocumentsListResponse>(
    'GET',
    `/api/projects/${projectId}/tracked-documents`
  );

export interface CreateTrackedDocumentBody {
  category: TrackedDocCategory;
  display_name: string;
  status?: TrackedDocStatus;
  private_notes?: string;
  sort_order?: number;
}

export interface TrackedDocumentResponse {
  tracked_document: TrackedDocument;
}

export const createTrackedDocument = (
  projectId: string,
  body: CreateTrackedDocumentBody
) =>
  request<TrackedDocumentResponse>(
    'POST',
    `/api/projects/${projectId}/tracked-documents`,
    body
  );

export interface PatchTrackedDocumentBody {
  category?: TrackedDocCategory;
  display_name?: string;
  status?: TrackedDocStatus;
  private_notes?: string;
  document_id?: string | null;
  sort_order?: number;
}

/**
 * D-049 / D-075 — when a PATCH transitions status to "Executed", the server
 * looks for an incomplete phase task whose text starts with the doc's
 * display_name (on a word boundary) and surfaces it here so the UI can
 * offer a one-click "mark task complete too" prompt without an extra
 * round-trip. `null` when the transition isn't to Executed, when the
 * project has no incomplete matching task, OR when 2+ tasks match
 * ambiguously (safety: rather miss than mis-match).
 */
export interface MatchingPhaseTask {
  task_id: string;
  task_text: string;
  task_group: string | null;
  is_critical: number;
  phase_id: string;
  phase_number: number;
  phase_name: string;
}

export interface PatchTrackedDocumentResponse {
  tracked_document: TrackedDocument;
  fields_updated: number;
  /**
   * Pre-D-078 single-match field. Populated only when exactly one
   * incomplete task matches; null otherwise. The UI keeps using this
   * for the simple "one click, mark complete" prompt.
   */
  matching_task: MatchingPhaseTask | null;
  /**
   * D-078 — full array of matching incomplete tasks. Length 0 = no
   * match, 1 = unique (mirrors `matching_task`), 2+ = ambiguous and
   * the UI renders a small picker so the user chooses.
   */
  matching_tasks: MatchingPhaseTask[];
}

export const patchTrackedDocument = (
  id: string,
  body: PatchTrackedDocumentBody
) =>
  request<PatchTrackedDocumentResponse>(
    'PATCH',
    `/api/tracked-documents/${id}`,
    body
  );

export const deleteTrackedDocument = (id: string) =>
  request<void>('DELETE', `/api/tracked-documents/${id}`);

// ============================================================================
// Templates — read-only surface used by Build B's "initialize from template"
// path. Full template management lands in Build E.
// ============================================================================

export interface TemplateSection {
  key: string;
  display_name: string;
}

export interface TemplateTrackedDoc {
  category: TrackedDocCategory;
  display_name: string;
}

/**
 * C.6 (D-056) — per-stage plan starter set in the template.
 * D-089: `discipline` enum replaced by free-text `stage`.
 */
export interface TemplateTrackedPlan {
  stage: string;
  display_name: string;
}

export interface ProjectTemplate {
  id: string;
  template_name: string;
  project_type: ProjectType;
  description: string | null;
  /** Server parses this column into `{ sections: TemplateSection[] }`. */
  notes_sections_json: { sections: TemplateSection[] } | string | null;
  /** Server parses this column into `{ documents: TemplateTrackedDoc[] }`. */
  tracked_documents_json:
    | { documents: TemplateTrackedDoc[] }
    | string
    | null;
  /** C.6 — server parses this column into `{ plans: TemplateTrackedPlan[] }`. */
  tracked_plans_json:
    | { plans: TemplateTrackedPlan[] }
    | string
    | null;
  /** Other JSON columns parsed but not used by client yet. */
  phases_json: unknown;
  folder_structure_json: unknown;
  is_default: number;
  is_user_created: number;
  created_at: string;
  updated_at: string;
}

export interface TemplateResponse {
  template: ProjectTemplate;
}

export interface TemplatesListResponse {
  templates: ProjectTemplate[];
  count: number;
}

export const getTemplate = (id: string) =>
  request<TemplateResponse>('GET', `/api/templates/${id}`);

export const listTemplates = (params?: {
  project_type?: ProjectType;
  is_user_created?: 0 | 1;
}) => {
  const q = new URLSearchParams();
  if (params?.project_type) q.set('project_type', params.project_type);
  if (params?.is_user_created != null)
    q.set('is_user_created', String(params.is_user_created));
  const qs = q.toString();
  return request<TemplatesListResponse>(
    'GET',
    `/api/templates${qs ? '?' + qs : ''}`
  );
};

export const deleteTemplate = (id: string) =>
  request<void>('DELETE', `/api/templates/${id}`);

// ----- Templates · Save from project (Build E.6 — D-074) -----

export interface SaveAsTemplateBody {
  project_id: string;
  template_name: string;
  description?: string | null;
}

export interface SaveAsTemplateResponse {
  template: ProjectTemplate;
  derived_from: {
    project_id: string;
    project_name: string;
    phase_count: number;
    task_count: number;
    section_count: number;
    tracked_document_count: number;
    tracked_plan_count: number;
  };
}

export const saveProjectAsTemplate = (body: SaveAsTemplateBody) =>
  request<SaveAsTemplateResponse>('POST', '/api/templates/from-project', body);

// ============================================================================
// Activity log (read surface used by Build B; compose modal lands in Build C)
// ============================================================================

export type LogType =
  | 'Call'
  | 'Meeting'
  | 'Email'
  | 'Site Visit'
  | 'Decision'
  | 'Issue'
  | 'Change Order'
  | 'Payment'
  | 'Permit'
  | 'Inspection'
  | 'RFI'
  | 'Submittal'
  | 'Note'
  // D-052 — added for per-permit comment threads (Build C.2). Reused
  // when Plans (C.6) and Pay Apps (C.7) ship.
  | 'Plan Review'
  | 'Engineer Response'
  | 'Approval';

// ============================================================================
// Phases (single-row GET) and Tasks — Build D.3
//
// Server already exposes:
//   GET    /api/projects/:id/phases       (returned via getProject too)
//   GET    /api/phases/:id                — single phase row
//   PATCH  /api/phases/:id                — date/status edits
//   POST   /api/phases/:id/cascade        — D-035 cascade endpoint
//   GET    /api/phases/:phaseId/tasks
//   POST   /api/phases/:phaseId/tasks     — ad-hoc task add
//   GET    /api/tasks/:id                 — single task + audit array
//   PATCH  /api/tasks/:id                 — partial update; is_complete writes audit
// ============================================================================

export interface Phase {
  id: string;
  project_id: string;
  phase_number: number;
  phase_name: string;
  phase_category: string | null;
  status: string;
  percent_complete: number;
  folder_path: string | null;
  is_visible_to_client: number;
  target_start_date: string | null;
  target_end_date: string | null;
  // D-118 — actual_start_date / actual_end_date dropped (migration 017).
  default_duration_weeks: number | null;
  published_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PhaseResponse {
  phase: Phase;
}

export const getPhase = (id: string) =>
  request<PhaseResponse>('GET', `/api/phases/${id}`);

export interface PatchPhaseBody {
  target_start_date?: string | null;
  target_end_date?: string | null;
  // D-118 — actual_*_date dropped from PATCH surface.
  default_duration_weeks?: number | null;
  status?: 'Not Started' | 'In Progress' | 'Complete';
  is_visible_to_client?: 0 | 1 | boolean;
  published_notes?: string | null;
}

export const patchPhase = (id: string, body: PatchPhaseBody) =>
  request<PhaseResponse>('PATCH', `/api/phases/${id}`, body);

/**
 * Cascade endpoint per D-035 — shifts every downstream phase's
 * target_start_date and target_end_date by `delta_days` (signed integer)
 * inside one transaction. Called by the date editor when the user opts
 * to cascade after changing a phase's target_end_date.
 */
export interface CascadeResponse {
  phases_shifted: number;
  phase_ids: string[];
  delta_days: number;
}

export const cascadePhase = (id: string, deltaDays: number) =>
  request<CascadeResponse>('POST', `/api/phases/${id}/cascade`, {
    delta_days: deltaDays,
  });

export interface Task {
  id: string;
  phase_id: string;
  project_id: string;
  task_number: number;
  task_text: string;
  task_description: string | null;
  task_group: string | null;
  document_id: string | null;
  familiarity_level: 'familiar' | 'partial' | 'new' | null;
  is_complete: number;
  is_critical: number;
  is_client_visible: number;
  published_note: string;
  /** D-111 — `task_note` column dropped. Notes attached to a task are now
   *  activity_log entries with task_id set. Render via the linked-update
   *  card under the task row + Recent Updates feed. */
  due_date: string | null;
  completed_date: string | null;
  has_unpublished_changes: number;
  created_at: string;
  updated_at: string;
}

export interface TasksListResponse {
  tasks: Task[];
  count: number;
}

export const listPhaseTasks = (phaseId: string) =>
  request<TasksListResponse>('GET', `/api/phases/${phaseId}/tasks`);

export interface CreateTaskBody {
  task_text: string;
  task_description?: string | null;
  task_group?: string | null;
  document_id?: string | null;
  is_critical?: 0 | 1 | boolean;
  is_client_visible?: 0 | 1 | boolean;
  due_date?: string | null;
  familiarity_level?: 'familiar' | 'partial' | 'new' | null;
}

export interface TaskResponse {
  task: Task;
}

export const createTask = (phaseId: string, body: CreateTaskBody) =>
  request<TaskResponse>('POST', `/api/phases/${phaseId}/tasks`, body);

export interface PatchTaskBody {
  is_complete?: 0 | 1 | boolean;
  task_text?: string;
  task_description?: string | null;
  task_group?: string | null;
  document_id?: string | null;
  due_date?: string | null;
  is_client_visible?: 0 | 1 | boolean;
  is_critical?: 0 | 1 | boolean;
  familiarity_level?: 'familiar' | 'partial' | 'new' | null;
  published_note?: string;
  has_unpublished_changes?: 0 | 1 | boolean;
  /** D-076 — drag-reorder writes back per-row task_number values within a task_group. */
  task_number?: number;
  /**
   * D-095 — backdate-able task completion timestamp. ISO 8601 string (or
   * null to clear). When supplied alongside `is_complete: 1`, this value
   * wins over the server's auto-stamp (the server normally stamps NOW
   * on the 0→1 flip). Use this to record when the task was *actually*
   * completed when the user is marking it complete after the fact.
   */
  completed_date?: string | null;
}

export const deleteTask = (id: string) =>
  request<void>('DELETE', `/api/tasks/${id}`);

export const patchTask = (id: string, body: PatchTaskBody) =>
  request<TaskResponse>('PATCH', `/api/tasks/${id}`, body);

// Cross-project alerts (D.4) — ARCH §4.6 "what needs attention" widget.
export type AlertCategory =
  | 'past_due'
  | 'due_this_week'
  | 'due_next_week'
  | 'critical_not_done';

export interface AlertTask {
  id: string;
  task_text: string;
  task_description: string | null;
  task_group: string | null;
  is_critical: number;
  due_date: string | null;
  phase_id: string;
  project_id: string;
  project_name: string;
  project_slug: string;
  color_tag: ColorTag;
  phase_number: number;
  phase_name: string;
  category: AlertCategory;
}

export interface AlertsResponse {
  tasks: AlertTask[];
  counts: Record<AlertCategory, number>;
  total: number;
}

export const listTaskAlerts = (params?: { project_id?: string }) => {
  const q = new URLSearchParams();
  if (params?.project_id) q.set('project_id', params.project_id);
  const qs = q.toString();
  return request<AlertsResponse>(
    'GET',
    `/api/alerts/tasks${qs ? '?' + qs : ''}`
  );
};

// ============================================================================
// Pay apps — AIA G702/G703 tracker (C.7, D-058)
//
// Permits-shaped (rich modal with structured fields). Each pay app carries
// workflow dates, multiple money columns, retainage tracking, a lien-waivers
// JSON sub-form, and a comment thread via activity_log.pay_app_id (mirrors
// D-052 permit_id pattern).
//
// Server: server/api/pay_apps.js. Disk: <project>/_PayApps/PayApp_<Number>/.
// Private to PM; never crosses to client portal.
// ============================================================================

export const PAY_APP_STATUSES = [
  'Draft',
  'Submitted',
  'Under Review',
  'Approved',
  'Paid',
  'Rejected',
] as const;
export type PayAppStatus = (typeof PAY_APP_STATUSES)[number];

export interface LienWaiver {
  party: string;
  type: 'Conditional' | 'Unconditional';
  status: 'Outstanding' | 'Received' | 'N/A';
  note?: string;
}

export interface PayApp {
  id: string;
  project_id: string;
  pay_app_number: string;
  period_start_date: string | null;
  period_end_date: string | null;
  submission_date: string | null;
  approval_date: string | null;
  payment_date: string | null;
  amount_requested: number | null;
  amount_approved: number | null;
  retainage_held: number | null;
  retainage_released: number | null;
  status: PayAppStatus;
  lien_waivers_json: { waivers: LienWaiver[] } | string | null;
  private_notes: string;
  document_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PayAppsListResponse {
  pay_apps: PayApp[];
  count: number;
}

export const listPayApps = (projectId: string) =>
  request<PayAppsListResponse>('GET', `/api/projects/${projectId}/pay-apps`);

export interface PayAppResponse {
  pay_app: PayApp;
}

export const getPayApp = (id: string) =>
  request<PayAppResponse>('GET', `/api/pay-apps/${id}`);

export interface CreatePayAppBody {
  pay_app_number: string;
  period_start_date?: string | null;
  period_end_date?: string | null;
  submission_date?: string | null;
  approval_date?: string | null;
  payment_date?: string | null;
  amount_requested?: number | null;
  amount_approved?: number | null;
  retainage_held?: number | null;
  retainage_released?: number | null;
  status?: PayAppStatus;
  lien_waivers_json?: { waivers: LienWaiver[] } | null;
  private_notes?: string;
  document_id?: string | null;
}

export const createPayApp = (projectId: string, body: CreatePayAppBody) =>
  request<PayAppResponse>('POST', `/api/projects/${projectId}/pay-apps`, body);

export interface PatchPayAppBody {
  pay_app_number?: string;
  period_start_date?: string | null;
  period_end_date?: string | null;
  submission_date?: string | null;
  approval_date?: string | null;
  payment_date?: string | null;
  amount_requested?: number | null;
  amount_approved?: number | null;
  retainage_held?: number | null;
  retainage_released?: number | null;
  status?: PayAppStatus;
  lien_waivers_json?: { waivers: LienWaiver[] } | null;
  private_notes?: string;
  document_id?: string | null;
}

export const patchPayApp = (id: string, body: PatchPayAppBody) =>
  request<PayAppResponse>('PATCH', `/api/pay-apps/${id}`, body);

export const deletePayApp = (id: string) =>
  request<void>('DELETE', `/api/pay-apps/${id}`);

export interface ActivityLogEntry {
  id: string;
  project_id: string;
  phase_id: string | null;
  task_id: string | null;
  section_key: string | null;
  /** D-052 — present after migration 005. NULL when the comment isn't
      tagged to a permit (e.g., regular Client Updates entries). */
  permit_id: string | null;
  /** D-058 — present after migration 007. NULL when the comment isn't
      tagged to a pay app. */
  pay_app_id: string | null;
  log_type: LogType;
  contact_id: string | null;
  contact_name_text: string | null;
  event_timestamp: string;
  private_entry: string;
  published_summary: string | null;
  is_published: number;
  manual_sort_order: number;
  has_unpublished_changes: number;
  /** D-106 — visual urgent flag mirroring tasks.is_critical. Sort order is
      NOT affected by this flag; the row stays where the manual_sort_order
      / event_timestamp sort puts it. Use the existing drag-reorder
      affordance on the Client Updates tab to pin an urgent row to top. */
  is_urgent: number;
  /** D-108 — clean prominent title (mirrors tasks.task_text). Nullable;
      when null, row renderers fall back to log_type + date. */
  entry_title: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActivityLogListResponse {
  entries: ActivityLogEntry[];
  count: number;
}

export interface ListActivityLogParams {
  is_published?: 0 | 1;
  section_key?: string;
  phase_id?: string;
  task_id?: string;
  /** D-052 — filter to a single permit's comment thread (Build C.2). */
  permit_id?: string;
  /** D-058 — filter to a single pay app's comment thread (Build C.7). */
  pay_app_id?: string;
  log_type?: LogType;
  contact_id?: string;
}

export const listActivityLog = (
  projectId: string,
  params?: ListActivityLogParams
) => {
  const q = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v != null) q.set(k, String(v));
    }
  }
  const qs = q.toString();
  return request<ActivityLogListResponse>(
    'GET',
    `/api/projects/${projectId}/activity-log${qs ? '?' + qs : ''}`
  );
};

export const VALID_LOG_TYPES: LogType[] = [
  'Call',
  'Meeting',
  'Email',
  'Site Visit',
  'Decision',
  'Issue',
  'Change Order',
  'Payment',
  'Permit',
  'Inspection',
  'RFI',
  'Submittal',
  'Note',
  // D-052 — added for the per-permit comment thread; reused on plans / pay apps later.
  'Plan Review',
  'Engineer Response',
  'Approval',
];

export interface CreateActivityLogBody {
  log_type: LogType;
  event_timestamp: string;
  section_key?: string | null;
  phase_id?: string | null;
  task_id?: string | null;
  /** D-052 — tag this comment to a permit so it appears in the permit's
      comment thread on the Permits page detail modal. */
  permit_id?: string | null;
  /** D-058 — tag this comment to a pay app so it appears in the pay app's
      comment thread on the Pay Apps page detail modal. */
  pay_app_id?: string | null;
  contact_id?: string | null;
  contact_name_text?: string | null;
  private_entry?: string;
  published_summary?: string;
  is_published?: 0 | 1 | boolean;
  manual_sort_order?: number;
  /** D-106 — visual urgent flag. */
  is_urgent?: 0 | 1 | boolean;
  /** D-108 — clean prominent title. */
  entry_title?: string | null;
}

export interface ActivityLogResponse {
  entry: ActivityLogEntry;
}

export const createActivityLogEntry = (
  projectId: string,
  body: CreateActivityLogBody
) =>
  request<ActivityLogResponse>(
    'POST',
    `/api/projects/${projectId}/activity-log`,
    body
  );

export interface PatchActivityLogBody {
  log_type?: LogType;
  event_timestamp?: string;
  section_key?: string | null;
  phase_id?: string | null;
  task_id?: string | null;
  permit_id?: string | null;
  pay_app_id?: string | null;
  contact_id?: string | null;
  contact_name_text?: string | null;
  private_entry?: string;
  published_summary?: string;
  is_published?: 0 | 1 | boolean;
  manual_sort_order?: number;
  has_unpublished_changes?: 0 | 1 | boolean;
  /** D-106 — visual urgent flag. */
  is_urgent?: 0 | 1 | boolean;
  /** D-108 — clean prominent title. */
  entry_title?: string | null;
}

export const patchActivityLogEntry = (
  id: string,
  body: PatchActivityLogBody
) => request<ActivityLogResponse>('PATCH', `/api/activity-log/${id}`, body);

export const deleteActivityLogEntry = (id: string) =>
  request<void>('DELETE', `/api/activity-log/${id}`);

// ============================================================================
// AI drafter — POST /api/ai/draft
//
// The drafter mode pulls the active section's notes + recent published
// updates as context (server-side, see server/api/ai.js buildContextFromDb).
// The UI just sends user_prompt + project_id + optional section_key.
// ============================================================================

export type AIProviderName = 'mock' | 'local' | 'cloud';
export type AIMode = 'drafter' | 'assist';

export interface AIDraftBody {
  project_id: string;
  user_prompt: string;
  section_key?: string;
  mode?: AIMode;
  provider?: AIProviderName;
}

export interface AIDraftResponse {
  ai_call_id: string;
  text: string;
  provider: string;
  model: string;
  tokens_in: number;
  tokens_out: number;
  cached_tokens: number;
  latency_ms: number;
  mode: AIMode;
}

export interface AIDraftError extends Error {
  status: number;
  body: unknown;
  /** 'unreachable' | 'http_error' | 'config_missing' */
  cause?: string;
}

export const aiDraft = (body: AIDraftBody) =>
  request<AIDraftResponse>('POST', '/api/ai/draft', body);

export interface AICallPatchBody {
  response_committed?: 0 | 1 | boolean;
  response_edited?: 0 | 1 | boolean;
}

export const patchAICall = (id: string, body: AICallPatchBody) =>
  request<{ ai_call: { id: string } }>('PATCH', `/api/ai/calls/${id}`, body);

// ============================================================================
// App settings (Build E.1 — D-065)
//
// Single-row config endpoint backing the Settings page. The two secret fields
// (`ai_cloud_api_key` + `supabase_service_key`) are NEVER returned on GET; the
// response includes `*_configured` booleans instead. PATCH accepts:
//   - undefined / omitted   = field not touched
//   - null                  = field not touched (used by the secret fields so
//                             the UI can omit them when unchanged)
//   - empty string ''       = clear the value (or for the secret fields,
//                             clear the stored secret)
//   - other value           = write
// ============================================================================

export type AIDefaultTarget = 'local' | 'cloud' | 'ask_per_call';

export interface AppSettings {
  pm_name: string | null;
  pm_email: string | null;
  pm_phone: string | null;

  documents_root: string | null;
  archive_root: string | null;
  backup_root: string | null;
  backup_interval_minutes: number | null;
  backup_retention_days: number | null;

  supabase_url: string | null;
  client_portal_base_url: string | null;
  supabase_service_key_configured: boolean;

  ai_local_endpoint: string | null;
  ai_local_model: string | null;
  ai_local_enabled: boolean | null;

  ai_cloud_provider: string | null;
  ai_cloud_model: string | null;
  ai_cloud_enabled: boolean | null;
  ai_cloud_api_key_configured: boolean;

  ai_default_target: AIDefaultTarget;

  /** JSON-encoded string array per migration 003. */
  indexer_ignore_folders: string | null;

  // D-082 — cloud publish + storage (Cloudflare D1 + R2). Public IDs cross
  // on GET; secret/access keys are stripped and replaced with `*_configured`
  // booleans. Same secret-key write-only pattern as ai_cloud_api_key per D-065.
  cloud_publish_provider: string | null;
  cloud_publish_account_id: string | null;
  cloud_publish_database_id: string | null;
  cloud_publish_secret_key_configured: boolean;
  cloud_storage_provider: string | null;
  cloud_storage_account_id: string | null;
  cloud_storage_bucket_name: string | null;
  cloud_storage_access_key_configured: boolean;
  cloud_storage_secret_key_configured: boolean;

  schema_version: string | null;
  last_backup_at: string | null;
  last_publish_at: string | null;
  /** D-079 — most-recent successful Layer 3 markdown export across all projects. */
  last_markdown_export_at: string | null;

  created_at: string | null;
  updated_at: string | null;
}

export interface AppSettingsPatch {
  pm_name?: string | null;
  pm_email?: string | null;
  pm_phone?: string | null;

  documents_root?: string | null;
  archive_root?: string | null;
  backup_root?: string | null;
  backup_interval_minutes?: number | null;
  backup_retention_days?: number | null;

  supabase_url?: string | null;
  /** Empty string clears; null is no-op. */
  supabase_service_key?: string | null;
  client_portal_base_url?: string | null;

  ai_local_endpoint?: string | null;
  ai_local_model?: string | null;
  ai_local_enabled?: boolean | null;

  ai_cloud_provider?: 'anthropic' | '' | null;
  ai_cloud_model?: string | null;
  ai_cloud_enabled?: boolean | null;
  /** Empty string clears; null is no-op. */
  ai_cloud_api_key?: string | null;

  ai_default_target?: AIDefaultTarget;

  /** Array OR JSON string OR null. */
  indexer_ignore_folders?: string[] | string | null;

  // D-082 — cloud publish + storage. Same secret-key semantics as
  // ai_cloud_api_key: null = no-op, '' = clear, non-empty string = write.
  cloud_publish_provider?: string | null;
  cloud_publish_account_id?: string | null;
  cloud_publish_database_id?: string | null;
  cloud_publish_secret_key?: string | null;
  cloud_storage_provider?: string | null;
  cloud_storage_account_id?: string | null;
  cloud_storage_bucket_name?: string | null;
  cloud_storage_access_key?: string | null;
  cloud_storage_secret_key?: string | null;
}

export const getAppSettings = () =>
  request<{ settings: AppSettings }>('GET', '/api/app-settings');

export const patchAppSettings = (body: AppSettingsPatch) =>
  request<{ settings: AppSettings; fields_updated: number }>(
    'PATCH',
    '/api/app-settings',
    body
  );

/** The locked folder convention (memory: project_folder_paths.md). */
export const DEFAULT_DOCUMENTS_ROOT = 'C:\\RoughInAdvisory\\01_Projects';
export const DEFAULT_ARCHIVE_ROOT = 'C:\\RoughInAdvisory\\02_Project_Archive';
/** D-078 — backup root default (matches `config.backupRoot` on the server). */
export const DEFAULT_BACKUP_ROOT = 'C:\\RoughInAdvisory\\99_Backups';
